import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import axios from 'axios';
import { useTelegram } from './hooks/useTelegram';
import './styles/common.css';
import LoadingScreen from './components/LoadingScreen';
import SearchBar from './components/SearchBar';
import SearchResults from './components/SearchResults';
import BottomNavigation from './components/BottomNavigation';
import HomeTab from './components/tabs/HomeTab';
import ChatsTab from './components/tabs/ChatsTab';
import FavoritesTab from './components/tabs/FavoritesTab';
import AboutTab from './components/tabs/AboutTab';
import BackgroundLogo from './components/BackgroundLogo';







// Основной компонент приложения
function App() {
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState('home');
  const [appData, setAppData] = useState(null);
  const [showPlatform, setShowPlatform] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Мемоизированные результаты поиска
  const searchResults = useMemo(() => {
    if (!searchQuery.trim() || !appData) return [];
    
    return appData.sections
      .flatMap(section => section.materials.map(material => ({
        ...material,
        uniqueKey: `${section.id}-${material.id}`
      })))
      .filter(material => 
        material.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
  }, [searchQuery, appData]);
  const [aboutActiveScreen, setAboutActiveScreen] = useState(null);

  // Используем Telegram хук
  const {
    isReady,
    isTelegramWebApp,
    getInitData,
    getTelegramId,
    showAlert,
    user
  } = useTelegram();

  // Обработка ошибок ResizeObserver
  useEffect(() => {
    const handleResizeObserverError = (e) => {
      if (e.message && e.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        // Игнорируем эту ошибку, так как она не критична
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const handleUnhandledRejection = (e) => {
      if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        // Игнорируем эту ошибку
        e.preventDefault();
        return false;
      }
    };

    // Обработка ошибок загрузки ресурсов
    const handleResourceError = (e) => {
      if (e.target && (e.target.tagName === 'IMG' || e.target.tagName === 'VIDEO')) {
        console.warn('Resource loading error:', e.target.src || e.target.href);
        // Скрываем элемент при ошибке загрузки
        if (e.target.style) {
          e.target.style.display = 'none';
        }
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleResourceError, true);
    
    return () => {
      window.removeEventListener('error', handleResizeObserverError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleResourceError, true);
    };
  }, []);

  // Функция для сохранения пользователя в БД
  const saveUserToDatabase = async (userData) => {
    try {
      const response = await fetch('/api/save-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.user_id;
    } catch (error) {
      console.error('Ошибка при сохранении пользователя:', error);
      // Возвращаем ID пользователя даже при ошибке для продолжения работы
      return userData.telegram_id;
    }
  };

  // Функция для получения данных пользователя из Telegram или создания дефолтного
  const getUserData = () => {
    if (isTelegramWebApp && user && user.id) {
      // Данные из Telegram WebApp
      return {
        telegram_id: user.id.toString(),
        first_name: user.first_name || null,
        last_name: user.last_name || null,
        username: user.username || null,
        language_code: user.language_code || null,
        is_bot: user.is_bot || false,
        is_premium: user.is_premium || false,
        added_to_attachment_menu: user.added_to_attachment_menu || false,
        allows_write_to_pm: user.allows_write_to_pm !== undefined ? user.allows_write_to_pm : true
      };
    } else {
      // Дефолтный пользователь для браузера
      return {
        telegram_id: "0000000000",
        first_name: "Гость",
        last_name: "Пользователь",
        username: "guest_dept_7890",
        language_code: "ru",
        is_bot: false,
        is_premium: false,
        added_to_attachment_menu: false,
        allows_write_to_pm: false
      };
    }
  };

  useEffect(() => {
    const initializeApp = async () => {
      try {
        setLoading(true);
        
        // Сохраняем пользователя в БД
        const userData = getUserData();
        const userId = await saveUserToDatabase(userData);
        
        // Загружаем данные приложения с учетом пользователя
        try {
          // Сначала пытаемся получить данные из API с user_id
          let appDataResponse;
          let dataSource = 'API';
          
          try {
            appDataResponse = await fetch(`/api/app-data?user_id=${userId}`);
            if (!appDataResponse.ok) {
              throw new Error(`API HTTP error! status: ${appDataResponse.status}`);
            }
          } catch (apiError) {
            console.log('API недоступен, используем fallback файл:', apiError.message);
            dataSource = 'fallback';
            
            // Fallback: загружаем из файла
            appDataResponse = await fetch('/app-data.json');
            
            if (!appDataResponse.ok) {
              throw new Error(`Fallback HTTP error! status: ${appDataResponse.status}`);
            }
          }
          
          const responseText = await appDataResponse.text();
          const appData = JSON.parse(responseText);
          setAppData(appData);
        } catch (appDataError) {
          console.error('Ошибка загрузки данных приложения:', appDataError);
          // Fallback: создаем базовые данные
          const fallbackData = {
            sections: [],
            favorites: [],
            configs: {
              show_section_banner: "true",
              fav_on: "false"
            }
          };
          setAppData(fallbackData);
        }
        
      } catch (error) {
        console.error('Error initializing app:', error);
        // showAlert('Ошибка загрузки приложения');
      } finally {
        setLoading(false);
      }
    };

    // Ждем готовности Telegram WebApp перед инициализацией
    if (isReady) {
      initializeApp();
    }
  }, [isReady, isTelegramWebApp, user]);

  const handleToggleFavorite = async (materialId) => {
    try {
      // Получаем ID пользователя
      const userData = getUserData();
      const userId = userData.telegram_id;
      
      // Обновляем локальное состояние оптимистично
      if (appData) {
        const material = appData.sections
          .flatMap(s => s.materials)
          .find(m => m.id === materialId);
        
        if (!material) {
          console.error('Material not found:', materialId);
          return;
        }
        
        const updatedSections = appData.sections.map(section => ({
          ...section,
          materials: section.materials.map(material => {
            if (material.id === materialId) {
              return { ...material, is_favorite: !material.is_favorite };
            }
            return material;
          })
        }));
        
        const updatedFavorites = material.is_favorite 
          ? appData.favorites.filter(id => id !== materialId)
          : [...appData.favorites, materialId];
        
        const newAppData = {
          ...appData,
          sections: updatedSections,
          favorites: updatedFavorites
        };
        
        setAppData(newAppData);
        
        // Обновляем selectedSection, если он существует
        if (selectedSection) {
          const updatedSelectedSection = updatedSections.find(section => section.id === selectedSection.id);
          if (updatedSelectedSection) {
            setSelectedSection(updatedSelectedSection);
          }
        }
        

      }
      
      // Синхронизируем с сервером
      try {
        const response = await fetch(`/api/favorites/${materialId}?user_id=${userId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
      } catch (apiError) {
        console.warn('API недоступен, изменения сохранены только локально:', apiError.message);
        // Можно добавить уведомление пользователю о том, что изменения не синхронизированы
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleOpenPlatform = () => {
    setShowPlatform(true);
    setCurrentTab('home');
  };

  const handleSectionSelect = useCallback((section) => {
    setSelectedSection(section);
  }, []);

  const handleBackToSections = useCallback(() => {
    setSelectedSection(null);
    // Прокручиваем к верху страницы
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleAboutActiveScreenChange = useCallback((screen) => {
    setAboutActiveScreen(screen);
  }, []);

  const handleBackFromAbout = useCallback(() => {
    setAboutActiveScreen(null);
    // Прокручиваем к верху страницы
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleAboutTabClick = useCallback(() => {
    // Добавляем небольшую задержку для предотвращения ошибок ResizeObserver
    setTimeout(() => {
      // Сбрасываем поиск при переходе
      setSearchQuery('');
      
      // Если мы уже на вкладке "О нас" и находимся на внутреннем экране, то возвращаемся к основному экрану
      if (currentTab === 'about' && aboutActiveScreen) {
        setAboutActiveScreen(null);
      } else {
        setCurrentTab('about');
      }
      
      // Прокручиваем к верху страницы
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }, [currentTab, aboutActiveScreen]);

  const handleTabChange = useCallback((tabName) => {
    // Добавляем небольшую задержку для предотвращения ошибок ResizeObserver
    setTimeout(() => {
      // Сбрасываем поиск при переходе на любую вкладку
      setSearchQuery('');
      setCurrentTab(tabName);
      
      // Сбрасываем состояние AboutTab при переходе на другие вкладки
      if (tabName !== 'about') {
        setAboutActiveScreen(null);
      }
      
      // Прокручиваем к верху страницы
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }, []);

  const handleHomeTabClick = useCallback(() => {
    // Добавляем небольшую задержку для предотвращения ошибок ResizeObserver
    setTimeout(() => {
      // Сбрасываем поиск при переходе
      setSearchQuery('');
      
      // Если мы уже на вкладке "Главное" и находимся в разделе, то возвращаемся к списку разделов
      if (currentTab === 'home' && selectedSection) {
        setSelectedSection(null);
      } else {
        setCurrentTab('home');
      }
      
      // Прокручиваем к верху страницы
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }, [currentTab, selectedSection]);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // Загрузочный экран
  if (loading) {
    return <LoadingScreen />;
  }

  // Экран с кнопкой "Открыть платформу" после загрузки данных
  if (!loading && !showPlatform) {
    return <LoadingScreen onOpenPlatform={handleOpenPlatform} />;
  }

  // Основное приложение
  return (
    <div className="App">
      <BackgroundLogo />
      <div className="app-container">
        {/* Поиск */}
        <SearchBar 
          searchQuery={searchQuery}
          onSearch={handleSearch}
          selectedSection={selectedSection}
          currentTab={currentTab}
          onBackToSections={() => {
            setSearchQuery('');
            handleBackToSections();
          }}
          aboutActiveScreen={aboutActiveScreen}
          onBackFromAbout={() => {
            setSearchQuery('');
            handleBackFromAbout();
          }}
        />

        {/* Основной контент */}
        <div className="main-content">
          {/* Результаты поиска */}
          <div className={`search-results-container ${searchQuery ? 'active' : 'hidden'}`}>
            <SearchResults 
              searchQuery={searchQuery}
              searchResults={searchResults}
              onToggleFavorite={handleToggleFavorite}
              appData={appData}
            />
          </div>

          {/* Основной контент */}
          <div className={`main-content-container ${!searchQuery ? 'active' : 'hidden'}`}>
            {currentTab === 'home' && (
              <HomeTab 
                appData={appData} 
                onToggleFavorite={handleToggleFavorite}
                selectedSection={selectedSection}
                onSectionSelect={handleSectionSelect}
                onBackToSections={handleBackToSections}
              />
            )}
            {currentTab === 'chats' && <ChatsTab appData={appData} onToggleFavorite={handleToggleFavorite} />}
            {currentTab === 'favorites' && <FavoritesTab appData={appData} onToggleFavorite={handleToggleFavorite} />}
            {currentTab === 'about' && (
              <AboutTab 
                appData={appData}
                activeScreen={aboutActiveScreen}
                onActiveScreenChange={handleAboutActiveScreenChange}
              />
            )}
          </div>
        </div>

        {/* Нижняя навигация */}
        <BottomNavigation 
          currentTab={currentTab}
          onHomeClick={handleHomeTabClick}
          onTabChange={handleTabChange}
          onAboutClick={handleAboutTabClick}
        />
      </div>
    </div>
  );
}

export default App;